/**
 * Diversity Stage — Stage 5c: Question type diversity validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.11
 *
 * Validates family-level question type diversity to prevent over-reliance
 * on safe/monotone question types (MC, fill_in).
 *
 * Runs as a BATCH check (like visualConsistencyBatch) — groups questions
 * by familyId and checks each family for diversity rules.
 *
 * Rules (from docs/guides/QUESTION_TYPE_DIVERSITY_RULES.md):
 * - DIV-001: ≥ 3 different question types per family (coach)
 * - DIV-002: ≥ 1 interactive type (not MC or fill_in) (coach)
 * - DIV-003: MC ≤ 50% of family (coach)
 * - DIV-004: ≥ 8 questions → ≥ 4 types (coach)
 * - DIV-005: ≥ 1 DOK 3 type recommended (info)
 * - DIV-006: No 3+ consecutive same type (info)
 * - DIV-007: fill_in ≤ 30% of family (info)
 *
 * All rules start as coach/info. Promotion to blocker planned for Phase 3 (Aug 2026).
 *
 * @see docs/guides/QUESTION_TYPE_DIVERSITY_RULES.md
 */

import type { ValidationIssue } from '../issueContract'
import { coach, info } from '../issueContract'
import type { Question, QuestionType } from '../../../content'

const STAGE = 5 as const

// ─── Interactive Type Set ─────────────────────────────────────────────────────

/** Types that count as "interactive" (not MC or fill_in) */
const INTERACTIVE_TYPES: ReadonlySet<string> = new Set<string>([
  'ordering',
  'matching',
  'categorize',
  'error_spotting',
  'true_false_series',
  'step_solver',
  'comparison_judge',
  'drag_drop_fill',
  'number_sequence',
  'estimation_slider',
  'grid_shade',
  'ratio_table',
  'text_highlight',
  'sentence_parser',
  'verb_conjugation_flow',
  'thermometer_slider',
  'sentence_error_judge',
  // Specialized types also count as interactive
  'cijferend_rekenen',
  'cijferend_delen',
  'clock',
  'money',
  'multiplication_grid',
  // Visual-data types count as interactive
  'bar_chart',
  'line_chart',
  'pie_chart',
  'schedule_reading',
  'table_reading',
])

/** Types that indicate DOK 3 capability */
const DOK3_TYPES: ReadonlySet<string> = new Set<string>([
  'error_spotting',
  'true_false_series',
  'categorize',
  'comparison_judge',
  'step_solver',
  'sentence_parser',
])

// ─── Thresholds ───────────────────────────────────────────────────────────────

/** Configurable thresholds for diversity checks */
export const DIVERSITY_THRESHOLDS = {
  /** Minimum different question types per family */
  minTypes: 3,
  /** Minimum types for families with ≥ 8 questions */
  minTypesLargeFamily: 4,
  /** Large family threshold (question count) */
  largeFamilySize: 8,
  /** Maximum MC percentage (0.0–1.0) */
  maxMcPercent: 0.5,
  /** Maximum fill_in percentage (0.0–1.0) */
  maxFillInPercent: 0.3,
  /** Maximum consecutive questions with same type */
  maxConsecutiveSame: 2,
} as const

// ─── Batch Diversity Check ────────────────────────────────────────────────────

/**
 * Run diversity checks on a batch of questions, grouped by family.
 * Returns issues attached to individual questions within each family.
 */
export function runDiversityBatch(questions: Question[]): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Group questions by familyId
  const families = new Map<string, Question[]>()
  for (const q of questions) {
    const fId = q.familyId ?? 'UNKNOWN'
    const existing = families.get(fId) ?? []
    existing.push(q)
    families.set(fId, existing)
  }

  // Check each family
  for (const [familyId, familyQuestions] of families) {
    if (familyQuestions.length < 2) continue // Skip tiny families

    const familyIssues = checkFamilyDiversity(familyId, familyQuestions)
    issues.push(...familyIssues)
  }

  return issues
}

// ─── Family-Level Checks ──────────────────────────────────────────────────────

function checkFamilyDiversity(
  familyId: string,
  questions: Question[],
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const firstQId = questions[0]?.id ?? 'UNKNOWN'

  const types = new Set(questions.map((q) => q.questionType))
  const typeCount = types.size
  const totalCount = questions.length

  const mcCount = questions.filter(
    (q) => q.questionType === 'multiple_choice',
  ).length
  const fillInCount = questions.filter(
    (q) => q.questionType === 'fill_in',
  ).length

  const hasInteractive = questions.some((q) =>
    INTERACTIVE_TYPES.has(q.questionType),
  )
  const hasDok3Type = questions.some((q) => DOK3_TYPES.has(q.questionType))

  // ── DIV-001: Minimum 3 types ──────────────────────────────────────────────

  if (typeCount < DIVERSITY_THRESHOLDS.minTypes) {
    issues.push(
      coach(
        'support',
        STAGE,
        'DIVERSITY_TOO_FEW_TYPES',
        `Family "${familyId}" heeft ${typeCount} vraagtype(s) (minimum: ${DIVERSITY_THRESHOLDS.minTypes}). Types: ${[...types].join(', ')}`,
        {
          questionId: firstQId,
          familyId,
          field: 'questionType',
          expected: `≥ ${DIVERSITY_THRESHOLDS.minTypes} verschillende vraagtypes`,
          actual: `${typeCount} type(s): ${[...types].join(', ')}`,
          suggestion:
            'Voeg interactieve vraagtypes toe zoals error_spotting, true_false_series, categorize, ordering, of step_solver.',
        },
      ),
    )
  }

  // ── DIV-002: At least 1 interactive type ──────────────────────────────────

  if (!hasInteractive) {
    issues.push(
      coach(
        'support',
        STAGE,
        'DIVERSITY_NO_INTERACTIVE',
        `Family "${familyId}" heeft geen interactief vraagtype (alleen MC/fill_in)`,
        {
          questionId: firstQId,
          familyId,
          field: 'questionType',
          expected: '≥ 1 interactief type (niet MC of fill_in)',
          actual: `Alleen: ${[...types].join(', ')}`,
          suggestion:
            'Voeg minimaal 1 interactief type toe: ordering, matching, categorize, error_spotting, true_false_series, step_solver, of comparison_judge.',
        },
      ),
    )
  }

  // ── DIV-003: MC max 50% ───────────────────────────────────────────────────

  const mcPercent = mcCount / totalCount
  if (mcPercent > DIVERSITY_THRESHOLDS.maxMcPercent) {
    issues.push(
      coach(
        'support',
        STAGE,
        'DIVERSITY_MC_OVERUSE',
        `Family "${familyId}": ${Math.round(mcPercent * 100)}% meerkeuze (max ${Math.round(DIVERSITY_THRESHOLDS.maxMcPercent * 100)}%)`,
        {
          questionId: firstQId,
          familyId,
          field: 'questionType',
          expected: `≤ ${Math.round(DIVERSITY_THRESHOLDS.maxMcPercent * 100)}% multiple_choice`,
          actual: `${mcCount}/${totalCount} = ${Math.round(mcPercent * 100)}%`,
          suggestion:
            'Vervang MC-vragen door interactieve types of voeg nieuwe interactieve vragen toe.',
        },
      ),
    )
  }

  // ── DIV-004: Large families need more variety ─────────────────────────────

  if (
    totalCount >= DIVERSITY_THRESHOLDS.largeFamilySize &&
    typeCount < DIVERSITY_THRESHOLDS.minTypesLargeFamily
  ) {
    issues.push(
      coach(
        'support',
        STAGE,
        'DIVERSITY_LOW_VARIETY',
        `Family "${familyId}" heeft ${totalCount} vragen maar slechts ${typeCount} types (aanbevolen: ≥ ${DIVERSITY_THRESHOLDS.minTypesLargeFamily})`,
        {
          questionId: firstQId,
          familyId,
          field: 'questionType',
          expected: `≥ ${DIVERSITY_THRESHOLDS.minTypesLargeFamily} types bij ≥ ${DIVERSITY_THRESHOLDS.largeFamilySize} vragen`,
          actual: `${typeCount} type(s)`,
          suggestion:
            'Voeg meer variatie toe — grotere families bieden ruimte voor diverse interacties.',
        },
      ),
    )
  }

  // ── DIV-005: DOK 3 type recommended ───────────────────────────────────────

  if (!hasDok3Type && totalCount >= 5) {
    issues.push(
      info(
        'support',
        STAGE,
        'DIVERSITY_NO_DOK3_TYPE',
        `Family "${familyId}" heeft geen DOK 3 vraagtype (error_spotting, true_false_series, categorize, comparison_judge, step_solver)`,
        {
          questionId: firstQId,
          familyId,
          suggestion:
            'Overweeg een analytisch type toe te voegen voor hogere cognitieve activatie.',
        },
      ),
    )
  }

  // ── DIV-006: Consecutive same type ────────────────────────────────────────

  let consecutiveCount = 1
  let maxConsecutive = 1
  let consecutiveType = ''

  for (let i = 1; i < questions.length; i++) {
    if (questions[i].questionType === questions[i - 1].questionType) {
      consecutiveCount++
      if (consecutiveCount > maxConsecutive) {
        maxConsecutive = consecutiveCount
        consecutiveType = questions[i].questionType
      }
    } else {
      consecutiveCount = 1
    }
  }

  if (maxConsecutive > DIVERSITY_THRESHOLDS.maxConsecutiveSame) {
    issues.push(
      info(
        'support',
        STAGE,
        'DIVERSITY_CONSECUTIVE_SAME',
        `Family "${familyId}": ${maxConsecutive} opeenvolgende "${consecutiveType}" vragen (max ${DIVERSITY_THRESHOLDS.maxConsecutiveSame})`,
        {
          questionId: firstQId,
          familyId,
          suggestion:
            'Wissel vraagtypes af voor een gevarieerde sessie-ervaring.',
        },
      ),
    )
  }

  // ── DIV-007: fill_in max 30% ──────────────────────────────────────────────

  const fillInPercent = fillInCount / totalCount
  if (fillInPercent > DIVERSITY_THRESHOLDS.maxFillInPercent) {
    issues.push(
      info(
        'support',
        STAGE,
        'DIVERSITY_FILL_IN_OVERUSE',
        `Family "${familyId}": ${Math.round(fillInPercent * 100)}% fill_in (max ${Math.round(DIVERSITY_THRESHOLDS.maxFillInPercent * 100)}%)`,
        {
          questionId: firstQId,
          familyId,
          field: 'questionType',
          suggestion:
            'Vervang fill_in-vragen door interactievere types zoals step_solver, matching, of number_sequence.',
        },
      ),
    )
  }

  return issues
}
